import * as v from 'valibot'

export type ProductReview = {
  id: string
  rating: number
  title: string
  comment: string
  submittedAt: string
  verified: boolean
}

export const productReviewSummarySchema = v.object({
  rating: v.pipe(v.number(), v.minValue(0), v.maxValue(5)),
  count: v.pipe(v.number(), v.integer(), v.minValue(0)),
})

export const productReviewSummariesSchema = v.record(v.string(), productReviewSummarySchema)

export type ProductReviewSummary = v.InferOutput<typeof productReviewSummarySchema>

export type ProductReviews = ProductReviewSummary & {
  distribution: number[]
  reviews: ProductReview[]
}

export const PRODUCT_REVIEW_SUMMARY_BATCH_SIZE = 50

export function parseProductReviewSummaries(input: unknown, publicIds?: readonly number[]) {
  const summaries = v.parse(productReviewSummariesSchema, input)
  if (publicIds?.some(publicId => !summaries[String(publicId)])) {
    throw new Error('Product rating response did not contain every requested experience')
  }
  return summaries
}

export type ProductReviewsPage = {
  items: {
    id: string
    type: string
    state: string
    status?: string
    product?: { sku: string }
    rating: number
    title?: string
    comment?: string
    submittedAt: string
    verificationType: string
  }[]
  paging?: { cursor?: { after?: string } }
}

/** Only approved reviews for this SKU contribute, including ratings without text. */
export function summarizeProductReviews(
  publicId: number,
  items: ProductReviewsPage['items']
): ProductReviews {
  const reviewsBySku = collectProductReviews([publicId], items)
  return aggregateProductReviews(reviewsBySku.get(`E-${publicId}`)?.values() ?? [])
}

export function summarizeProductReviewRatings(
  publicIds: readonly number[],
  items: ProductReviewsPage['items']
): Record<string, ProductReviewSummary> {
  const reviewsBySku = collectProductReviews(publicIds, items)

  return Object.fromEntries(
    publicIds.map(publicId => {
      const { rating, count } = aggregateProductReviews(
        reviewsBySku.get(`E-${publicId}`)?.values() ?? []
      )
      return [String(publicId), { rating, count }]
    })
  )
}

function collectProductReviews(publicIds: readonly number[], items: ProductReviewsPage['items']) {
  const requestedSkus = new Set(publicIds.map(publicId => `E-${publicId}`))
  const reviewsBySku = new Map<string, Map<string, ProductReview>>()

  for (const item of items) {
    const sku = item.product?.sku
    if (!sku || !requestedSkus.has(sku) || !isApprovedProductRating(item)) continue

    const reviews = reviewsBySku.get(sku) ?? new Map<string, ProductReview>()
    reviews.set(item.id, toProductReview(item))
    reviewsBySku.set(sku, reviews)
  }

  return reviewsBySku
}

function toProductReview(item: ProductReviewsPage['items'][number]): ProductReview {
  return {
    id: item.id,
    rating: item.rating,
    title: item.title ?? '',
    comment: item.comment ?? '',
    submittedAt: item.submittedAt,
    verified: item.verificationType === 'VERIFIED',
  }
}

function aggregateProductReviews(reviewsInput: Iterable<ProductReview>): ProductReviews {
  const reviews = [...reviewsInput].toSorted((a, b) => {
    const commentPriority = Number(Boolean(b.comment.trim())) - Number(Boolean(a.comment.trim()))
    return commentPriority || b.submittedAt.localeCompare(a.submittedAt)
  })
  const distribution = [0, 0, 0, 0, 0]
  let sum = 0

  for (const review of reviews) {
    distribution[5 - review.rating]++
    sum += review.rating
  }

  return {
    rating: reviews.length ? sum / reviews.length : 0,
    count: reviews.length,
    distribution,
    reviews,
  }
}

function isApprovedProductRating(item: ProductReviewsPage['items'][number]) {
  return (
    item.type === 'PRODUCT_REVIEW' &&
    item.state === 'APPROVED' &&
    (!item.status || item.status === 'APPROVED') &&
    Number.isInteger(item.rating) &&
    item.rating >= 1 &&
    item.rating <= 5
  )
}
